import React, { useState } from 'react';
import { Row, Col, Card, Table, Badge, Form, Button } from 'react-bootstrap';
import { 
  BarChart, Bar, PieChart, Pie, LineChart, Line, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, AreaChart, Area,
  ScatterChart, Scatter, ZAxis
} from 'recharts';
import { FaFileDownload, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

// Utils
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value);
};

const formatNumber = (value, decimals = 1) => {
  if (value === null || value === undefined) return '0';
  return value.toLocaleString('en-US', { 
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Color palette for charts
const COLORS = [
  '#3366CC', '#DC3912', '#FF9900', '#109618', '#990099', 
  '#0099C6', '#DD4477', '#66AA00', '#B82E2E', '#316395'
];

const LaborReport = ({ data, viewMode = 'both' }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [searchTerm, setSearchTerm] = useState('');
  
  if (!data || !data.data) {
    return <div>No data available</div>;
  }
  
  const reportType = data.type;
  let reportData = data.data;
  
  // Calculate summary statistics
  const calculateSummary = () => {
    if (reportType === 'labor-hours') {
      const totalHours = reportData.summary?.totalHours || 0;
      const totalProjects = reportData.summary?.projectCount || 0;
      const totalEmployees = reportData.summary?.employeeCount || 0;
      
      return {
        totalHours,
        totalProjects,
        totalEmployees,
        avgHoursPerProject: totalProjects > 0 ? totalHours / totalProjects : 0
      };
    } else if (reportType === 'labor-costs') {
      const totalCost = reportData.summary?.totalCost || 0;
      const totalHours = reportData.summary?.totalHours || 0;
      
      return {
        totalCost,
        totalHours,
        avgHourlyCost: totalHours > 0 ? totalCost / totalHours : 0,
        projectCount: reportData.summary?.projectCount || 0
      };
    } else if (reportType === 'overtime-analysis') {
      const totalHours = reportData.summary?.totalHours || 0;
      const overtimeHours = reportData.summary?.overtimeHours || 0;
      
      return {
        totalHours,
        overtimeHours,
        overtimePercentage: totalHours > 0 ? overtimeHours / totalHours : 0,
        overtimeCost: reportData.summary?.overtimeCost || 0
      };
    } else if (reportType === 'employee-productivity') {
      return {
        averageProductivity: reportData.summary?.averageProductivity || 0,
        highestProductivity: reportData.summary?.highestProductivity || 0,
        lowestProductivity: reportData.summary?.lowestProductivity || 0,
        employeeCount: reportData.summary?.employeeCount || 0
      };
    }
    
    return {};
  };
  
  const summary = calculateSummary();
  
  // Sort table data
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };
  
  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return <FaSort className="ms-1 text-muted" />;
    }
    return sortConfig.direction === 'ascending' ? 
      <FaSortUp className="ms-1" /> : <FaSortDown className="ms-1" />;
  };
  
  const getSortedData = (items) => {
    if (!items || !sortConfig.key) return items;
    
    const sortableItems = [...items];
    sortableItems.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle null values
      if (aValue === null) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (bValue === null) return sortConfig.direction === 'ascending' ? 1 : -1;
      
      // For strings, ignore case
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      // Dates need special handling
      if (sortConfig.key === 'Date' || sortConfig.key === 'EntryDate') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
    
    return sortableItems;
  };
  
  // Filter table data
  const getFilteredData = (items) => {
    if (!items || !searchTerm) return items;
    
    return items.filter(item => {
      // Search through all string and number fields
      return Object.keys(item).some(key => {
        const value = item[key];
        if (value === null || value === undefined) return false;
        
        if (typeof value === 'string') {
          return value.toLowerCase().includes(searchTerm.toLowerCase());
        } else if (typeof value === 'number') {
          return value.toString().includes(searchTerm);
        }
        
        return false;
      });
    });
  };
  
  // Render labor hours report
  const renderLaborHours = () => {
    // Filter and sort time entries
    let timeEntries = reportData.entries || [];
    if (searchTerm) {
      timeEntries = getFilteredData(timeEntries);
    }
    if (sortConfig.key) {
      timeEntries = getSortedData(timeEntries);
    }
    
    // Hours by employee chart
    const hoursByEmployee = reportData.byEmployee || [];
    
    // Hours by project chart
    const hoursByProject = reportData.byProject || [];
    
    // Hours by day/week chart
    const hoursByTime = reportData.byTime || [];
    
    const renderCharts = () => (
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Hours by Employee</h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={hoursByEmployee}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value} hours`, 'Hours']} />
                  <Legend />
                  <Bar dataKey="hours" name="Hours" fill="#8884d8">
                    {hoursByEmployee.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Hours by Project</h5>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={hoursByProject}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="hours"
                    nameKey="name"
                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {hoursByProject.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} hours`, 'Hours']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
    
    const renderLaborTable = () => (
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Labor Hours Details</h5>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Search entries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ width: '200px' }}
              />
              <Button 
                variant="outline-secondary" 
                size="sm"
                title="Export as CSV"
              >
                <FaFileDownload />
              </Button>
            </div>
          </div>
          
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th onClick={() => requestSort('EntryDate')} className="sortable">
                  Date {getSortIcon('EntryDate')}
                </th>
                <th onClick={() => requestSort('EmployeeName')} className="sortable">
                  Employee {getSortIcon('EmployeeName')}
                </th>
                <th onClick={() => requestSort('ProjectName')} className="sortable">
                  Project {getSortIcon('ProjectName')}
                </th>
                <th onClick={() => requestSort('TaskDescription')} className="sortable">
                  Task {getSortIcon('TaskDescription')}
                </th>
                <th onClick={() => requestSort('Hours')} className="sortable">
                  Hours {getSortIcon('Hours')}
                </th>
                <th onClick={() => requestSort('Billable')} className="sortable">
                  Billable {getSortIcon('Billable')}
                </th>
              </tr>
            </thead>
            <tbody>
              {timeEntries.map((entry, index) => (
                <tr key={index}>
                  <td>{formatDate(entry.EntryDate)}</td>
                  <td>{entry.EmployeeName}</td>
                  <td>{entry.ProjectName}</td>
                  <td>{entry.TaskDescription}</td>
                  <td>{formatNumber(entry.Hours, 2)}</td>
                  <td>{entry.Billable ? 'Yes' : 'No'}</td>
                </tr>
              ))}
              {timeEntries.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center">No time entries found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
    
    return (
      <>
        <div className="summary-cards">
          <div className="summary-card">
            <h5>Total Hours</h5>
            <h3>{formatNumber(summary.totalHours, 1)}</h3>
          </div>
          <div className="summary-card">
            <h5>Total Employees</h5>
            <h3>{summary.totalEmployees}</h3>
          </div>
          <div className="summary-card">
            <h5>Total Projects</h5>
            <h3>{summary.totalProjects}</h3>
          </div>
          <div className="summary-card">
            <h5>Avg Hours/Project</h5>
            <h3>{formatNumber(summary.avgHoursPerProject, 1)}</h3>
          </div>
        </div>
        
        {(viewMode === 'chart' || viewMode === 'both') && renderCharts()}
        {(viewMode === 'table' || viewMode === 'both') && renderLaborTable()}
      </>
    );
  };
  
  // Render labor costs report
  const renderLaborCosts = () => {
    // Filter and sort labor costs
    let laborCosts = reportData.costs || [];
    if (searchTerm) {
      laborCosts = getFilteredData(laborCosts);
    }
    if (sortConfig.key) {
      laborCosts = getSortedData(laborCosts);
    }
    
    // Cost by project chart
    const costByProject = reportData.byProject || [];
    
    // Cost by employee chart
    const costByEmployee = reportData.byEmployee || [];
    
    // Cost trend chart
    const costTrend = reportData.costTrend || [];
    
    const renderCharts = () => (
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Labor Costs by Project</h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={costByProject}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis 
                    tickFormatter={(value) => `$${Math.floor(value / 1000)}k`}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Cost']}
                  />
                  <Legend />
                  <Bar dataKey="cost" name="Labor Cost" fill="#8884d8">
                    {costByProject.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Cost Trend</h5>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={costTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis 
                    tickFormatter={(value) => `$${Math.floor(value / 1000)}k`}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), '']}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="cost" name="Labor Cost" stroke="#8884d8" strokeWidth={2} />
                  <Line type="monotone" dataKey="billable" name="Billable Amount" stroke="#82ca9d" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
    
    const renderCostsTable = () => (
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Labor Costs Details</h5>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ width: '200px' }}
              />
              <Button 
                variant="outline-secondary" 
                size="sm"
                title="Export as CSV"
              >
                <FaFileDownload />
              </Button>
            </div>
          </div>
          
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th onClick={() => requestSort('Period')} className="sortable">
                  Period {getSortIcon('Period')}
                </th>
                <th onClick={() => requestSort('ProjectName')} className="sortable">
                  Project {getSortIcon('ProjectName')}
                </th>
                <th onClick={() => requestSort('EmployeeName')} className="sortable">
                  Employee {getSortIcon('EmployeeName')}
                </th>
                <th onClick={() => requestSort('Hours')} className="sortable">
                  Hours {getSortIcon('Hours')}
                </th>
                <th onClick={() => requestSort('Rate')} className="sortable">
                  Rate {getSortIcon('Rate')}
                </th>
                <th onClick={() => requestSort('Cost')} className="sortable">
                  Cost {getSortIcon('Cost')}
                </th>
                <th onClick={() => requestSort('BillableAmount')} className="sortable">
                  Billable Amount {getSortIcon('BillableAmount')}
                </th>
              </tr>
            </thead>
            <tbody>
              {laborCosts.map((cost, index) => (
                <tr key={index}>
                  <td>{cost.Period}</td>
                  <td>{cost.ProjectName}</td>
                  <td>{cost.EmployeeName}</td>
                  <td>{formatNumber(cost.Hours, 2)}</td>
                  <td>{formatCurrency(cost.Rate)}</td>
                  <td>{formatCurrency(cost.Cost)}</td>
                  <td>{formatCurrency(cost.BillableAmount)}</td>
                </tr>
              ))}
              {laborCosts.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center">No labor cost data found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
    
    return (
      <>
        <div className="summary-cards">
          <div className="summary-card">
            <h5>Total Cost</h5>
            <h3>{formatCurrency(summary.totalCost)}</h3>
          </div>
          <div className="summary-card">
            <h5>Total Hours</h5>
            <h3>{formatNumber(summary.totalHours, 1)}</h3>
          </div>
          <div className="summary-card">
            <h5>Avg Hourly Cost</h5>
            <h3>{formatCurrency(summary.avgHourlyCost)}</h3>
          </div>
          <div className="summary-card">
            <h5>Project Count</h5>
            <h3>{summary.projectCount}</h3>
          </div>
        </div>
        
        {(viewMode === 'chart' || viewMode === 'both') && renderCharts()}
        {(viewMode === 'table' || viewMode === 'both') && renderCostsTable()}
      </>
    );
  };
  
  // Render overtime analysis report
  const renderOvertimeAnalysis = () => {
    // Filter and sort overtime entries
    let overtimeEntries = reportData.entries || [];
    if (searchTerm) {
      overtimeEntries = getFilteredData(overtimeEntries);
    }
    if (sortConfig.key) {
      overtimeEntries = getSortedData(overtimeEntries);
    }
    
    // Overtime by employee chart
    const overtimeByEmployee = reportData.byEmployee || [];
    
    // Overtime by day chart
    const overtimeByDay = reportData.byDay || [];
    
    const renderCharts = () => (
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Overtime Hours by Employee</h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={overtimeByEmployee} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" width={150} />
                  <Tooltip formatter={(value) => [`${formatNumber(value, 1)} hours`, 'Overtime']} />
                  <Legend />
                  <Bar dataKey="overtime" name="Overtime Hours" fill="#ff8042" />
                  <Bar dataKey="regular" name="Regular Hours" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Overtime Trend</h5>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={overtimeByDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${formatNumber(value, 1)} hours`, '']} />
                  <Legend />
                  <Line type="monotone" dataKey="overtime" name="Overtime Hours" stroke="#ff8042" strokeWidth={2} />
                  <Line type="monotone" dataKey="total" name="Total Hours" stroke="#8884d8" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
    
    const renderOvertimeTable = () => (
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Overtime Details</h5>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Search entries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ width: '200px' }}
              />
              <Button 
                variant="outline-secondary" 
                size="sm"
                title="Export as CSV"
              >
                <FaFileDownload />
              </Button>
            </div>
          </div>
          
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th onClick={() => requestSort('Date')} className="sortable">
                  Date {getSortIcon('Date')}
                </th>
                <th onClick={() => requestSort('EmployeeName')} className="sortable">
                  Employee {getSortIcon('EmployeeName')}
                </th>
                <th onClick={() => requestSort('ProjectName')} className="sortable">
                  Project {getSortIcon('ProjectName')}
                </th>
                <th onClick={() => requestSort('RegularHours')} className="sortable">
                  Regular Hours {getSortIcon('RegularHours')}
                </th>
                <th onClick={() => requestSort('OvertimeHours')} className="sortable">
                  Overtime Hours {getSortIcon('OvertimeHours')}
                </th>
                <th onClick={() => requestSort('OvertimeCost')} className="sortable">
                  Overtime Cost {getSortIcon('OvertimeCost')}
                </th>
              </tr>
            </thead>
            <tbody>
              {overtimeEntries.map((entry, index) => (
                <tr key={index}>
                  <td>{formatDate(entry.Date)}</td>
                  <td>{entry.EmployeeName}</td>
                  <td>{entry.ProjectName}</td>
                  <td>{formatNumber(entry.RegularHours, 2)}</td>
                  <td className="text-warning">{formatNumber(entry.OvertimeHours, 2)}</td>
                  <td>{formatCurrency(entry.OvertimeCost)}</td>
                </tr>
              ))}
              {overtimeEntries.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center">No overtime entries found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
    
    return (
      <>
        <div className="summary-cards">
          <div className="summary-card">
            <h5>Total Hours</h5>
            <h3>{formatNumber(summary.totalHours, 1)}</h3>
          </div>
          <div className="summary-card">
            <h5>Overtime Hours</h5>
            <h3 className="text-warning">{formatNumber(summary.overtimeHours, 1)}</h3>
          </div>
          <div className="summary-card">
            <h5>Overtime %</h5>
            <h3 className="text-warning">{(summary.overtimePercentage * 100).toFixed(1)}%</h3>
          </div>
          <div className="summary-card">
            <h5>Overtime Cost</h5>
            <h3 className="text-warning">{formatCurrency(summary.overtimeCost)}</h3>
          </div>
        </div>
        
        {(viewMode === 'chart' || viewMode === 'both') && renderCharts()}
        {(viewMode === 'table' || viewMode === 'both') && renderOvertimeTable()}
      </>
    );
  };
  
  // Render employee productivity report
  const renderEmployeeProductivity = () => {
    // Filter and sort productivity data
    let productivityData = reportData.employees || [];
    if (searchTerm) {
      productivityData = getFilteredData(productivityData);
    }
    if (sortConfig.key) {
      productivityData = getSortedData(productivityData);
    }
    
    // Productivity by employee chart
    const productivityByEmployee = reportData.byEmployee || [];
    
    // Productivity vs. efficiency scatter plot
    const productivityVsEfficiency = reportData.productivityVsEfficiency || [];
    
    const renderCharts = () => (
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Productivity by Employee</h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={productivityByEmployee}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${formatNumber(value, 2)}`, 'Productivity Score']} />
                  <Legend />
                  <Bar dataKey="productivity" name="Productivity Score" fill="#8884d8">
                    {productivityByEmployee.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Productivity vs. Efficiency</h5>
              <ResponsiveContainer width="100%" height={300}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    type="number" 
                    dataKey="efficiency" 
                    name="Efficiency" 
                    unit="%" 
                    domain={[0, 100]}
                  />
                  <YAxis 
                    type="number" 
                    dataKey="productivity" 
                    name="Productivity" 
                    unit=" pts"
                  />
                  <ZAxis 
                    type="number" 
                    dataKey="hours" 
                    range={[50, 400]} 
                    name="Hours"
                  />
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                  <Legend />
                  <Scatter 
                    name="Employees" 
                    data={productivityVsEfficiency} 
                    fill="#8884d8"
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
    
    const renderProductivityTable = () => (
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Employee Productivity Details</h5>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ width: '200px' }}
              />
              <Button 
                variant="outline-secondary" 
                size="sm"
                title="Export as CSV"
              >
                <FaFileDownload />
              </Button>
            </div>
          </div>
          
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th onClick={() => requestSort('EmployeeName')} className="sortable">
                  Employee {getSortIcon('EmployeeName')}
                </th>
                <th onClick={() => requestSort('Department')} className="sortable">
                  Department {getSortIcon('Department')}
                </th>
                <th onClick={() => requestSort('TotalHours')} className="sortable">
                  Total Hours {getSortIcon('TotalHours')}
                </th>
                <th onClick={() => requestSort('TasksCompleted')} className="sortable">
                  Tasks Completed {getSortIcon('TasksCompleted')}
                </th>
                <th onClick={() => requestSort('AvgHoursPerTask')} className="sortable">
                  Avg Hours/Task {getSortIcon('AvgHoursPerTask')}
                </th>
                <th onClick={() => requestSort('Efficiency')} className="sortable">
                  Efficiency {getSortIcon('Efficiency')}
                </th>
                <th onClick={() => requestSort('ProductivityScore')} className="sortable">
                  Productivity Score {getSortIcon('ProductivityScore')}
                </th>
              </tr>
            </thead>
            <tbody>
              {productivityData.map((employee, index) => (
                <tr key={index}>
                  <td>{employee.EmployeeName}</td>
                  <td>{employee.Department}</td>
                  <td>{formatNumber(employee.TotalHours, 1)}</td>
                  <td>{employee.TasksCompleted}</td>
                  <td>{formatNumber(employee.AvgHoursPerTask, 2)}</td>
                  <td>{formatNumber(employee.Efficiency, 1)}%</td>
                  <td>
                    <span className={
                      employee.ProductivityScore > 80 ? 'text-success' : 
                      employee.ProductivityScore > 60 ? 'text-primary' : 
                      employee.ProductivityScore > 40 ? 'text-warning' : 'text-danger'
                    }>
                      {formatNumber(employee.ProductivityScore, 1)}
                    </span>
                  </td>
                </tr>
              ))}
              {productivityData.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center">No employee productivity data found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
    
    return (
      <>
        <div className="summary-cards">
          <div className="summary-card">
            <h5>Average Productivity</h5>
            <h3>{formatNumber(summary.averageProductivity, 1)}</h3>
          </div>
          <div className="summary-card">
            <h5>Highest Productivity</h5>
            <h3 className="text-success">{formatNumber(summary.highestProductivity, 1)}</h3>
          </div>
          <div className="summary-card">
            <h5>Lowest Productivity</h5>
            <h3 className="text-warning">{formatNumber(summary.lowestProductivity, 1)}</h3>
          </div>
          <div className="summary-card">
            <h5>Employee Count</h5>
            <h3>{summary.employeeCount}</h3>
          </div>
        </div>
        
        {(viewMode === 'chart' || viewMode === 'both') && renderCharts()}
        {(viewMode === 'table' || viewMode === 'both') && renderProductivityTable()}
      </>
    );
  };
  
  // Render the appropriate report based on the report type
  switch (reportType) {
    case 'labor-hours':
      return renderLaborHours();
    case 'labor-costs':
      return renderLaborCosts();
    case 'overtime-analysis':
      return renderOvertimeAnalysis();
    case 'employee-productivity':
      return renderEmployeeProductivity();
    default:
      return <div>Unknown report type</div>;
  }
};

export default LaborReport;